import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { FolderTree } from "lucide-react";

// ============ BULK MOVE CATEGORY (with lock) ============
export default function BulkMoveCategoryDialog({ open, onOpenChange, selectedIds, categories, onDone }) {
  const [targetSlug, setTargetSlug] = useState("");
  const [lock, setLock] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) { setTargetSlug(""); setLock(true); }
  }, [open]);

  const submit = async () => {
    if (!targetSlug) {
      toast.error("Hedef kategori seç");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/products/bulk-move-category", {
        ids: selectedIds, category_slug: targetSlug, lock,
      });
      toast.success(`✓ ${data.moved} ürün taşındı${data.locked ? " ve 🔒 kilitlendi" : ""}`);
      onOpenChange(false);
      // Wait for Radix close animation (~200ms) before triggering parent
      // reload — otherwise the mass re-render races with portal unmount
      // → "removeChild: node is not a child of this node".
      setTimeout(() => onDone?.(), 240);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Taşınamadı");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-move-dialog" className="bg-[#FAF7F2] border-[#E8E2D5] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FolderTree className="w-5 h-5 text-[#2E5A36]" />
            {selectedIds.length} Ürünü Taşı
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Hedef Kategori</Label>
            <Select value={targetSlug} onValueChange={setTargetSlug}>
              <SelectTrigger data-testid="move-target-select" className="bg-white">
                <SelectValue placeholder="Kategori seç..." />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.slug} value={c.slug}>
                    {c.icon || "📦"} {c.name_tr}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-start gap-2 p-3 rounded-xl bg-[#E5A024]/10 border border-[#E5A024]/40 cursor-pointer">
            <input
              type="checkbox"
              data-testid="move-lock-checkbox"
              checked={lock}
              onChange={(e) => setLock(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#2E5A36]"
            />
            <div>
              <p className="text-sm font-heading font-bold text-[#B4711C]">
                🔒 Bir sonraki Wolvox senkronizasyonundan koru
              </p>
              <p className="text-xs text-[#1B2A22]/70 mt-1">
                İşaretlerseniz Akınsoft'tan gelen grup değişse bile ürün bu kategoride kalır.
                Kaldırmak için ürünü düzenle ekranından "Kategori kilidini aç"ı kullanın.
              </p>
            </div>
          </label>
          <Button
            data-testid="move-submit-btn"
            onClick={submit}
            disabled={submitting || !targetSlug}
            className="w-full bg-[#2E5A36] hover:bg-[#1B2A22] text-[#FAF7F2] rounded-full h-11 font-heading font-bold"
          >
            {submitting ? "Taşınıyor..." : "Taşı"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
